import React from 'react';
import { CheckCircle, Users, Target, Heart, Award, Leaf } from 'lucide-react';
import { companyInfo } from '../mockData';

const AboutPage = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E5631] to-[#2d7a47] py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.pexels.com/photos/1453499/pexels-photo-1453499.jpeg"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl text-white">
            <span className="inline-block bg-[#4CAF50] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              About Us
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
              About Udyaan Landscaping
            </h1>
            <p className="text-xl text-green-100 leading-relaxed">
              Creating beautiful, sustainable outdoor environments that enhance quality of life
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.pexels.com/photos/1453499/pexels-photo-1453499.jpeg"
                alt="Professional landscaper"
                className="rounded-2xl shadow-xl w-full h-[500px] object-cover"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-block text-[#4CAF50] font-semibold text-sm uppercase tracking-wide mb-4">
                Our Mission
              </span>
              <h2 className="font-heading text-4xl font-bold text-gray-900 mb-6">
                Transforming Spaces, Enriching Lives
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {companyInfo.about.mission}
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                {companyInfo.about.experience}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {companyInfo.about.philosophy}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#4CAF50] font-semibold text-sm uppercase tracking-wide mb-4">
              Our Core Values
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              The Principles That Guide Us
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Commitment to excellence in every project we undertake
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {companyInfo.about.values.map((value, index) => {
              const icons = [Users, Target, Heart, CheckCircle, Award, Leaf];
              const Icon = icons[index] || CheckCircle;
              
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  <div className="bg-gradient-to-br from-[#1E5631] to-[#2d7a47] rounded-full w-16 h-16 flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-gray-900 mb-4">{value}</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We are deeply committed to {value.toLowerCase()} in everything we do, ensuring exceptional results for every client.
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-gradient-to-r from-[#1E5631] to-[#2d7a47] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1558904541-efa843a96f01"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="text-center">
              <div className="text-5xl font-bold text-[#4CAF50] mb-2">100+</div>
              <div className="text-xl text-green-100">Projects Completed</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#4CAF50] mb-2">50+</div>
              <div className="text-xl text-green-100">Happy Clients</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#4CAF50] mb-2">10+</div>
              <div className="text-xl text-green-100">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#4CAF50] mb-2">15+</div>
              <div className="text-xl text-green-100">Expert Team Members</div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#4CAF50] font-semibold text-sm uppercase tracking-wide mb-4">
              Our Expertise
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Dedicated Team of Professionals
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Expert horticulture and landscaping specialists committed to excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-[#1E5631] to-[#2d7a47] text-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-[#4CAF50] rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-4">
                Landscape Architects
              </h3>
              <p className="text-green-100 leading-relaxed">
                Certified professionals with expertise in design, planning, and spatial composition for outdoor environments.
              </p>
            </div>
            <div className="bg-gradient-to-br from-[#1E5631] to-[#2d7a47] text-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-[#4CAF50] rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <Leaf className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-4">
                Horticulture Specialists
              </h3>
              <p className="text-green-100 leading-relaxed">
                Plant experts with deep knowledge of local flora, soil conditions, and sustainable growing practices.
              </p>
            </div>
            <div className="bg-gradient-to-br from-[#1E5631] to-[#2d7a47] text-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-[#4CAF50] rounded-full w-16 h-16 flex items-center justify-center mb-6">
                <Award className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-semibold mb-4">
                Installation Crew
              </h3>
              <p className="text-green-100 leading-relaxed">
                Skilled technicians who bring designs to life with precision and attention to every detail.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Get expert consultation and bring your landscape vision to life
          </p>
          <a
            href="/contact"
            className="inline-block bg-gradient-to-r from-[#1E5631] to-[#2d7a47] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
          >
            Contact Us Today
          </a>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
